import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { FingerprintAIO } from '@ionic-native/fingerprint-aio/ngx';
import { ServiceService } from './service.service';

export interface BiometricCredentials {
  usuario: string;
  password: string;
}

const BIOMETRIC_CREDENTIALS_KEY = 'biometric_credentials';
const BIOMETRIC_ENABLED_KEY = 'biometricLoginEnabled';
const BIOMETRIC_FAILED_ATTEMPTS_KEY = 'biometric_failed_attempts';
const MAX_FAILED_ATTEMPTS = 3;
const ENCRYPTION_PASS = 'BiometricSecure2024ED';

@Injectable({
  providedIn: 'root'
})
export class BiometricAuthService {

  constructor(
    private platform: Platform,
    private faio: FingerprintAIO,
    private service: ServiceService
  ) {}

  /**
   * Verifica si el dispositivo soporta biometría
   */
  async isAvailable(): Promise<boolean> {
    // Solo disponible en mobile
    if (!this.isMobile()) {
      return false;
    }

    try {
      await this.faio.isAvailable();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica si es plataforma mobile
   */
  isMobile(): boolean {
    return this.platform.is('cordova') ||
           this.platform.is('android') ||
           this.platform.is('ios');
  }

  /**
   * Verifica si el usuario tiene biometría habilitada para login
   */
  async isBiometricEnabled(): Promise<boolean> {
    if (!this.isMobile()) {
      return false;
    }

    const enabled = localStorage.getItem(BIOMETRIC_ENABLED_KEY);
    if (enabled !== 'true') {
      return false;
    }

    // Verificar que también existan las credenciales guardadas
    const credentials = localStorage.getItem(BIOMETRIC_CREDENTIALS_KEY);
    return credentials !== null && credentials !== '';
  }

  /**
   * Habilita biometría y guarda las credenciales de forma segura
   */
  async enableBiometric(credentials: BiometricCredentials): Promise<boolean> {
    if (!await this.isAvailable()) {
      return false;
    }

    try {
      // Primero validar que la biometría funcione
      await this.faio.show({
        title: 'Habilitar acceso biométrico',
        description: 'Confirmá tu identidad para habilitar el acceso con huella/rostro',
        disableBackup: false,
        fallbackButtonTitle: 'Cancelar'
      });

      // Encriptar y guardar credenciales
      const encryptedCredentials = this.service.encrypt(credentials, ENCRYPTION_PASS);
      localStorage.setItem(BIOMETRIC_CREDENTIALS_KEY, encryptedCredentials);
      localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');

      // También habilitar el flag existente de la app para consistencia
      localStorage.setItem('conDatoBiometrico', 'si');

      // Resetear intentos fallidos
      localStorage.setItem(BIOMETRIC_FAILED_ATTEMPTS_KEY, '0');

      return true;
    } catch (error) {
      console.error('[BiometricAuth] Error al habilitar biometría:', error);
      return false;
    }
  }

  /**
   * Deshabilita biometría y elimina las credenciales guardadas
   */
  async disableBiometric(): Promise<boolean> {
    try {
      localStorage.removeItem(BIOMETRIC_CREDENTIALS_KEY);
      localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
      localStorage.removeItem(BIOMETRIC_FAILED_ATTEMPTS_KEY);
      return true;
    } catch (error) {
      console.error('[BiometricAuth] Error al deshabilitar biometría:', error);
      return false;
    }
  }

  /**
   * Autentica con biometría y retorna las credenciales guardadas
   */
  async authenticateAndGetCredentials(): Promise<BiometricCredentials | null> {
    if (!this.isMobile()) {
      return null;
    }

    // Verificar límite de intentos fallidos
    const failedAttempts = parseInt(localStorage.getItem(BIOMETRIC_FAILED_ATTEMPTS_KEY) || '0');
    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      console.warn('[BiometricAuth] Demasiados intentos fallidos, deshabilitando biometría');
      await this.disableBiometric();
      return null;
    }

    try {
      const nombre = localStorage.getItem('nombre') || 'Usuario';

      await this.faio.show({
        title: 'Hola ' + nombre,
        description: 'Usá tu huella o rostro para ingresar a la app',
        disableBackup: false,
        fallbackButtonTitle: 'Cancelar'
      });

      // Resetear intentos fallidos en éxito
      localStorage.setItem(BIOMETRIC_FAILED_ATTEMPTS_KEY, '0');

      // Obtener y desencriptar credenciales
      return this.getCredentials();
    } catch (error) {
      console.error('[BiometricAuth] Error en autenticación biométrica:', error);

      // Incrementar contador de intentos fallidos
      const currentAttempts = parseInt(localStorage.getItem(BIOMETRIC_FAILED_ATTEMPTS_KEY) || '0');
      localStorage.setItem(BIOMETRIC_FAILED_ATTEMPTS_KEY, (currentAttempts + 1).toString());

      return null;
    }
  }

  /**
   * Solo valida biometría sin retornar credenciales (para MFA)
   */
  async validateBiometric(): Promise<boolean> {
    if (!this.isMobile()) {
      return false;
    }

    // Verificar que tenga biometría habilitada (usa el flag existente de la app)
    const biometricEnabled = localStorage.getItem('conDatoBiometrico') === 'si' ||
                             localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';

    if (!biometricEnabled) {
      return false;
    }

    try {
      const nombre = localStorage.getItem('nombre') || 'Usuario';

      await this.faio.show({
        title: 'Verificación de identidad',
        description: 'Confirmá tu identidad para continuar con la operación',
        disableBackup: false,
        fallbackButtonTitle: 'Usar código'
      });

      return true;
    } catch (error) {
      console.error('[BiometricAuth] Error en validación biométrica:', error);
      return false;
    }
  }

  /**
   * Verifica si puede usar biometría para MFA (transferencias)
   */
  async canUseBiometricForMfa(): Promise<boolean> {
    const isAvailable = await this.isAvailable();
    const biometricEnabled = localStorage.getItem('conDatoBiometrico') === 'si' ||
                             localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';

    return isAvailable && biometricEnabled;
  }

  /**
   * Obtiene las credenciales guardadas (desencriptadas)
   */
  private getCredentials(): BiometricCredentials | null {
    try {
      const encryptedCredentials = localStorage.getItem(BIOMETRIC_CREDENTIALS_KEY);
      if (!encryptedCredentials) {
        return null;
      }

      const decrypted = this.service.decrypt(encryptedCredentials, ENCRYPTION_PASS);
      if (!decrypted || !decrypted.usuario || !decrypted.password) {
        return null;
      }

      return decrypted as BiometricCredentials;
    } catch (error) {
      console.error('[BiometricAuth] Error al obtener credenciales:', error);
      return null;
    }
  }

  /**
   * Invalida las credenciales guardadas (llamar cuando el login falla)
   */
  async invalidateCredentials(): Promise<void> {
    console.warn('[BiometricAuth] Invalidando credenciales guardadas');
    await this.disableBiometric();
  }

  /**
   * Verifica si el usuario ya rechazó habilitar biometría
   */
  hasDeclinedBiometric(): boolean {
    return localStorage.getItem('biometricDeclined') === 'true';
  }

  /**
   * Marca que el usuario rechazó habilitar biometría
   */
  setDeclinedBiometric(): void {
    localStorage.setItem('biometricDeclined', 'true');
  }

  /**
   * Marca para recordar más tarde
   */
  setRemindLater(): void {
    const remindDate = new Date();
    remindDate.setDate(remindDate.getDate() + 3); // Recordar en 3 días
    localStorage.setItem('biometricRemindDate', remindDate.toISOString());
  }

  /**
   * Verifica si debe mostrar el prompt de habilitación
   */
  shouldShowEnablePrompt(): boolean {
    // Si ya está habilitado, no mostrar
    if (localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true') {
      return false;
    }

    // Si rechazó permanentemente, no mostrar
    if (this.hasDeclinedBiometric()) {
      return false;
    }

    // Verificar si hay fecha de recordatorio
    const remindDateStr = localStorage.getItem('biometricRemindDate');
    if (remindDateStr) {
      const remindDate = new Date(remindDateStr);
      if (new Date() < remindDate) {
        return false;
      }
    }

    return true;
  }
}
